// Rename File/Folder Modal
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { AudioFile, Folder } from '@types';
import { useFileStore } from '@stores/useFileStore';
import { useToast } from '@components/ui';
import { SUSPENSION_ERROR_MESSAGE } from '@utils/suspension';

interface RenameModalProps {
  file: AudioFile | (Folder & { type?: 'folder' });
  onClose: () => void;
  onSuccess?: () => void;
}

export const RenameModal: React.FC<RenameModalProps> = ({ file, onClose, onSuccess }) => {
  const [newName, setNewName] = useState(file.name);
  const [isLoading, setIsLoading] = useState(false);
  const { updateFile, updateFolder } = useFileStore();
  const { error: showError } = useToast();
  const isFolder = 'type' in file && file.type === 'folder';

  useEffect(() => {
    // Focus input on mount
    const input = document.getElementById('rename-input');
    if (input) {
      (input as HTMLInputElement).select();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim() === '' || newName === file.name) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      if (isFolder) {
        await updateFolder(file.id, { name: newName.trim() });
      } else {
        await updateFile(file.id, { name: newName.trim() });
      }
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error(`Failed to rename ${isFolder ? 'folder' : 'file'}:`, error);
      if (error.message === 'ACCOUNT_SUSPENDED') {
        showError(SUSPENSION_ERROR_MESSAGE.title, SUSPENSION_ERROR_MESSAGE.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-3">
      <div className="w-full max-w-sm sm:max-w-md bg-dark-secondary/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-2.5 sm:p-3 border-b border-white/10">
          <div className="flex-1 min-w-0 pr-2">
            <h2 className="font-dela text-sm sm:text-base text-white truncate">
              Rename {isFolder ? 'Folder' : 'File'}
            </h2>
            <p className="text-xs text-white/60 truncate mt-0.5">{file.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-3 sm:p-4">
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="rename-input"
              className="block mb-1.5 font-inter font-bold text-xs text-white/80"
            >
              New Name
            </label>
            <input
              id="rename-input"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white font-inter focus:outline-none focus:border-primary transition-colors"
              placeholder="Enter new file name"
              autoFocus
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 text-sm bg-white/10 hover:bg-white/20 text-white rounded-lg font-inter font-bold transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || newName.trim() === ''}
              className="flex-1 px-3 py-2 text-sm bg-gradient-to-r from-[#30d870]/80 to-[#00ff61]/80 hover:from-[#30d870] hover:to-[#00ff61] text-black rounded-lg font-inter font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Renaming...' : 'Rename'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

